import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../App';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

function StatCard({ label, value, sub, color = 'text-white', icon }) {
  return (
    <div className="bg-gray-900 border border-gray-800 rounded-2xl p-5">
      <div className="text-xl mb-3">{icon}</div>
      <div className={`text-3xl font-bold ${color} mb-1`}>{value}</div>
      <div className="text-sm text-gray-400">{label}</div>
      {sub && <div className="text-xs text-gray-600 mt-1">{sub}</div>}
    </div>
  );
}

function MiniBar({ label, value, max, color = '#e6b300' }) {
  const pct = max > 0 ? Math.min(100, (value / max) * 100) : 0;
  return (
    <div className="mb-3">
      <div className="flex justify-between text-xs text-gray-400 mb-1">
        <span>{label}</span>
        <span className="text-white font-medium">${(value || 0).toFixed(2)}</span>
      </div>
      <div className="h-1.5 bg-gray-800 rounded-full overflow-hidden">
        <div className="h-full rounded-full transition-all duration-500" style={{ width: `${pct}%`, background: color }} />
      </div>
    </div>
  );
}

function PendingPayouts({ token }) {
  const [groups, setGroups] = useState(null);
  const [paying, setPaying] = useState(null); // driverId currently being paid
  const [msg, setMsg] = useState('');

  async function load() {
    try {
      const r = await fetch(`${API_URL}/api/finance/pending-payouts`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const d = await r.json();
      setGroups(d.drivers || []);
    } catch (e) {
      setGroups([]);
    }
  }

  useEffect(() => { load(); }, []);

  async function payDriver(group) {
    if (!group.payoutsEnabled) return;
    setPaying(group.driverId);
    setMsg('');
    let ok = 0;
    let failed = 0;
    // One transfer per delivery (preserves per-delivery audit trail).
    for (const d of group.deliveries) {
      try {
        const r = await fetch(`${API_URL}/api/finance/deliveries/${d.deliveryId}/payout`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await r.json();
        if (r.ok && data.success) ok += 1; else failed += 1;
      } catch (e) {
        failed += 1;
      }
    }
    setPaying(null);
    setMsg(`${group.name}: paid ${ok} deliver${ok === 1 ? 'y' : 'ies'}${failed ? `, ${failed} failed` : ''}.`);
    load(); // refresh owed amounts
  }

  if (groups === null) {
    return (
      <div className="bg-gray-900 border border-gray-800 rounded-2xl p-6">
        <h2 className="font-semibold mb-2 flex items-center gap-2"><span>🧾</span> Pending Payouts</h2>
        <p className="text-gray-600 text-sm">Loading…</p>
      </div>
    );
  }

  return (
    <div className="bg-gray-900 border border-gray-800 rounded-2xl p-6">
      <h2 className="font-semibold mb-5 flex items-center gap-2"><span>🧾</span> Pending Payouts</h2>
      {msg && <p className="text-xs text-gray-400 mb-4">{msg}</p>}
      {groups.length === 0 ? (
        <p className="text-gray-500 text-sm">No pending payouts — everyone's paid up.</p>
      ) : (
        <div className="space-y-3">
          {groups.map((g) => (
            <div key={g.driverId} className="flex items-center justify-between border-b border-gray-800 last:border-0 pb-3">
              <div>
                <p className="text-white font-medium">{g.name}</p>
                <p className="text-xs text-gray-500 mt-0.5">
                  {g.deliveries.length} deliver{g.deliveries.length === 1 ? 'y' : 'ies'} •
                  {' '}base ${g.baseOwed.toFixed(2)}{g.tipOwed > 0 ? ` + tips $${g.tipOwed.toFixed(2)}` : ''}
                </p>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-yellow-400 font-bold font-mono">${g.totalOwed.toFixed(2)}</span>
                {g.payoutsEnabled ? (
                  <button
                    onClick={() => payDriver(g)}
                    disabled={paying === g.driverId}
                    className="px-4 py-2 bg-green-500 hover:bg-green-400 text-white text-sm font-bold rounded-xl transition disabled:opacity-50"
                  >
                    {paying === g.driverId ? 'Paying…' : `Pay all $${g.totalOwed.toFixed(2)}`}
                  </button>
                ) : (
                  <span className="text-xs text-yellow-400/80">Not onboarded</span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function FinancialDashboard() {
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API_URL}/api/finance/dashboard`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.json())
      .then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-yellow-400 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!data || data.error) {
    return (
      <div className="min-h-screen bg-black flex flex-col items-center justify-center text-gray-400 gap-4">
        <div className="text-5xl">📊</div>
        <p className="text-lg">No financial data yet</p>
        <p className="text-sm text-gray-600">Start processing deliveries to see your numbers here</p>
        <Link to="/admin" className="mt-4 px-6 py-2.5 bg-yellow-400 text-black font-bold rounded-xl hover:bg-yellow-300 transition">
          Back to Admin
        </Link>
      </div>
    );
  }

  const { summary, revenue, byPlan, dailyDeliveries } = data;
  const maxRevenue = dailyDeliveries?.length > 0 ? Math.max(...dailyDeliveries.map(d => d.revenue || 0), 1) : 1;
  const planColors = { starter: '#666', business: '#e6b300', enterprise: '#22c55e', null: '#444' };

  return (
    <div className="min-h-screen bg-black text-white">
      <header className="border-b border-gray-800 px-4 md:px-8 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Link to="/admin" className="text-gray-400 hover:text-white transition">← Admin</Link>
          <span className="text-gray-600">|</span>
          <h1 className="font-bold text-lg">Financial Dashboard</h1>
        </div>
        <div className="text-xs text-gray-500 font-mono">
          {new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
        </div>
      </header>

      <main className="px-4 md:px-8 py-8 max-w-7xl mx-auto space-y-8">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard icon="💰" label="Total Revenue This Month" value={`$${(revenue.totalRevenue || 0).toFixed(2)}`} color="text-yellow-400" sub="Deliveries + subscriptions" />
          <StatCard icon="📦" label="Net Margin This Month" value={`$${(revenue.netMargin || 0).toFixed(2)}`} color="text-green-400" sub={`${revenue.marginPercent || 0}% margin rate`} />
          <StatCard icon="🚗" label="Driver Payouts This Month" value={`$${(revenue.driverCosts || 0).toFixed(2)}`} color="text-blue-400" sub={`${summary.monthDeliveries} deliveries`} />
          <StatCard icon="🏢" label="Subscription Revenue" value={`$${(revenue.subscriptionRevenue || 0).toFixed(2)}`} color="text-purple-400" sub={`${summary.totalRestaurants} restaurants`} />
        </div>

        <PendingPayouts token={token} />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-gray-900 border border-gray-800 rounded-2xl p-6">
            <h2 className="font-semibold mb-5 flex items-center gap-2"><span>💵</span> Revenue Breakdown</h2>
            <MiniBar label="Delivery Revenue" value={revenue.deliveryRevenue} max={revenue.totalRevenue} color="#e6b300" />
            <MiniBar label="Subscription Revenue" value={revenue.subscriptionRevenue} max={revenue.totalRevenue} color="#a855f7" />
            <MiniBar label="Driver Costs" value={revenue.driverCosts} max={revenue.totalRevenue} color="#3b82f6" />
            <div className="mt-5 pt-4 border-t border-gray-800 space-y-2">
              {[
                { label: 'Gross Revenue', value: revenue.totalRevenue, color: 'text-yellow-400' },
                { label: 'Driver Payouts', value: -(revenue.driverCosts || 0), color: 'text-red-400' },
                { label: 'Net Margin', value: revenue.netMargin, color: 'text-green-400' },
              ].map(row => (
                <div key={row.label} className="flex justify-between items-center text-sm">
                  <span className="text-gray-400">{row.label}</span>
                  <span className={`font-bold font-mono ${row.color}`}>
                    {row.value >= 0 ? '+' : ''}${Math.abs(row.value || 0).toFixed(2)}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-gray-900 border border-gray-800 rounded-2xl p-6">
            <h2 className="font-semibold mb-5 flex items-center gap-2"><span>📋</span> Restaurant Plan Mix</h2>
            {byPlan && byPlan.length > 0 ? (
              <div className="space-y-4">
                {byPlan.map(p => (
                  <div key={p._id || 'none'} className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-3 h-3 rounded-full" style={{ background: planColors[p._id] || '#666' }} />
                      <span className="text-sm capitalize text-gray-300">{p._id || 'No Plan'}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-24 h-1.5 bg-gray-800 rounded-full overflow-hidden">
                        <div className="h-full rounded-full" style={{ width: `${(p.count / Math.max(summary.totalRestaurants, 1)) * 100}%`, background: planColors[p._id] || '#666' }} />
                      </div>
                      <span className="text-sm font-bold text-white w-6 text-right">{p.count}</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-sm">No restaurants yet</p>
            )}
            <div className="mt-6 pt-4 border-t border-gray-800">
              <h3 className="text-xs text-gray-500 font-mono uppercase tracking-wider mb-3">Projected Monthly @ Current Mix</h3>
              {[
                { plan: 'Starter', count: byPlan?.find(p => p._id === 'starter')?.count || 0, base: 0, perDelivery: 8, avg: 15 },
                { plan: 'Business', count: byPlan?.find(p => p._id === 'business')?.count || 0, base: 199, perDelivery: 7, avg: 30 },
                { plan: 'Enterprise', count: byPlan?.find(p => p._id === 'enterprise')?.count || 0, base: 899, perDelivery: 6.5, avg: 150 },
              ].map(p => (
                <div key={p.plan} className="flex justify-between text-xs text-gray-400 mb-1">
                  <span>{p.plan} ({p.count} restaurants)</span>
                  <span className="text-yellow-400">${(p.count * (p.base + p.avg * p.perDelivery)).toFixed(0)}/mo</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="bg-gray-900 border border-gray-800 rounded-2xl p-6">
          <h2 className="font-semibold mb-6 flex items-center gap-2"><span>📈</span> Daily Revenue — Last 30 Days</h2>
          {dailyDeliveries && dailyDeliveries.length > 0 ? (
            <div className="flex items-end gap-1 h-32 overflow-x-auto pb-2">
              {dailyDeliveries.map(day => (
                <div key={day._id} className="flex flex-col items-center gap-1 flex-shrink-0" style={{ minWidth: '28px' }}>
                  <div className="w-5 rounded-sm" title={`${day._id}: $${day.revenue} — ${day.count} deliveries`}
                    style={{ height: `${Math.max(4, (day.revenue / maxRevenue) * 100)}px`, background: '#e6b300', opacity: 0.8 }} />
                  <span className="text-gray-700" style={{ fontSize: '9px' }}>{day._id?.slice(8)}</span>
                </div>
              ))}
            </div>
          ) : (
            <div className="h-32 flex items-center justify-center text-gray-600 text-sm">
              Revenue chart appears once deliveries are completed
            </div>
          )}
        </div>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {[
            { label: 'All-Time Deliveries', value: summary.totalDeliveries, icon: '📦' },
            { label: 'This Month', value: summary.monthDeliveries, icon: '📅' },
            { label: 'This Week', value: summary.weekDeliveries, icon: '🗓' },
            { label: 'Active Drivers', value: summary.totalDrivers, icon: '🚗' },
            { label: 'Restaurants', value: summary.totalRestaurants, icon: '🍽️' },
          ].map(s => (
            <div key={s.label} className="bg-gray-900 border border-gray-800 rounded-xl p-4 text-center">
              <div className="text-lg mb-1">{s.icon}</div>
              <div className="text-2xl font-bold text-yellow-400">{s.value || 0}</div>
              <div className="text-xs text-gray-500 mt-1">{s.label}</div>
            </div>
          ))}
        </div>

        <div className="bg-gray-900 border border-gray-800 rounded-2xl p-6">
          <h2 className="font-semibold mb-5 flex items-center gap-2"><span>🧮</span> Per-Delivery Economics</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              { plan: 'Starter', charge: 8, driverPay: 5, margin: 3, marginPct: '37.5%', color: 'border-gray-700' },
              { plan: 'Business', charge: 7, driverPay: 5, margin: 2, marginPct: '28.6%', color: 'border-yellow-400/50' },
              { plan: 'Enterprise', charge: 6.5, driverPay: 5, margin: 1.5, marginPct: '23.1%', color: 'border-green-400/50' },
            ].map(p => (
              <div key={p.plan} className={`border ${p.color} rounded-xl p-4`}>
                <div className="text-yellow-400 font-bold font-mono text-sm mb-3 uppercase tracking-wider">{p.plan}</div>
                {[
                  { label: 'You charge', value: `$${p.charge}`, color: 'text-white' },
                  { label: 'Driver pay', value: `-$${p.driverPay}`, color: 'text-red-400' },
                  { label: 'Your margin', value: `$${p.margin}`, color: 'text-green-400' },
                  { label: 'Margin %', value: p.marginPct, color: 'text-yellow-400' },
                ].map(row => (
                  <div key={row.label} className="flex justify-between text-sm py-1 border-b border-gray-800 last:border-0">
                    <span className="text-gray-400">{row.label}</span>
                    <span className={`font-bold font-mono ${row.color}`}>{row.value}</span>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      </main>
    </div>
  );
}
